import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUIStore } from '@/stores/uiStore';
import { useChatSessions } from '@/hooks/useChatSessions';
import { formatRelativeTime, truncate } from '@/lib/utils';
import { debounce } from '@/lib/utils';

export function ChatHistoryModal() {
  const navigate = useNavigate();
  const { modals, closeModal } = useUIStore();
  const {
    sessions,
    isLoading,
    error,
    currentPage,
    totalPages,
    search,
    setSearch,
    loadPage,
    deleteSession,
  } = useChatSessions({ limit: 10, autoLoad: false });

  const [searchInput, setSearchInput] = useState('');
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load sessions when modal opens
  useEffect(() => {
    if (modals.chatHistory) {
      loadPage(1);
    }
  }, [modals.chatHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
    }, 300),
    [setSearch]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Reload when search changes
  useEffect(() => {
    if (modals.chatHistory && search !== undefined) {
      loadPage(1);
    }
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSessionClick = (sessionId: string) => {
    closeModal('chatHistory');
    navigate(`/chat?session=${sessionId}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setDeleteSessionId(sessionId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteSessionId) return;

    setIsDeleting(true);
    try {
      await deleteSession(deleteSessionId);
    } catch (err) {
      console.error('Failed to delete session:', err);
    } finally {
      setIsDeleting(false);
      setDeleteSessionId(null);
    }
  };

  const handleClose = () => {
    setSearchInput('');
    setSearch('');
    closeModal('chatHistory');
  };

  return (
    <>
      <Dialog open={modals.chatHistory} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>채팅 기록</DialogTitle>
            <DialogDescription>
              이전 채팅 대화를 검색하고 선택하세요
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="채팅 검색..."
              value={searchInput}
              onChange={handleSearchChange}
              className="pl-9"
            />
          </div>

          {/* Session List */}
          <ScrollArea className="h-[300px]">
            {isLoading ? (
              <div className="space-y-2 p-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-2">
                    <Skeleton className="h-8 w-8 rounded" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
                {error}
              </div>
            ) : sessions.length === 0 ? (
              <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
                {search ? '검색 결과가 없습니다' : '채팅 기록이 없습니다'}
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {sessions.map((session) => (
                  <button
                    key={session.id}
                    onClick={() => handleSessionClick(session.id)}
                    className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-accent"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-muted">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate text-sm font-medium">
                        {truncate(session.title || '새 채팅', 30)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatRelativeTime(session.updatedAt)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={(e) => handleDeleteClick(e, session.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1 || isLoading}
                onClick={() => loadPage(currentPage - 1)}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                이전
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages || isLoading}
                onClick={() => loadPage(currentPage + 1)}
              >
                다음
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteSessionId} onOpenChange={() => setDeleteSessionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>채팅 삭제</AlertDialogTitle>
            <AlertDialogDescription>
              이 채팅을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? '삭제 중...' : '삭제'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
